import type { CSSProperties } from "react";

export type SchedulingStatus =
  | "pending"
  | "scheduled"
  | "confirmed"
  | "canceled"
  | "finished"
  | "attended";

export interface StatusConfig {
  /** The semantic token of the status, as a CSS custom property name. */
  token: string;
  /** Soft chip ground + neutral text; the dot carries the colour. */
  chip: string;
  dot: string;
  label: string;
}

/**
 * Status is shown with colour and shape (a dot, a pill), never colour alone
 * (design-system.md §2). The colours are the semantic tokens, so the agenda
 * reads like the rest of the product.
 */
const statusMap: Record<SchedulingStatus, StatusConfig> = {
  pending: {
    token: "--app-warning",
    chip: "bg-app-warning/10 text-app-fg-strong",
    dot: "bg-app-warning",
    label: "Pendente",
  },
  scheduled: {
    token: "--app-info",
    chip: "bg-app-info/10 text-app-fg-strong",
    dot: "bg-app-info",
    label: "Agendado",
  },
  confirmed: {
    token: "--app-success",
    chip: "bg-app-success/10 text-app-fg-strong",
    dot: "bg-app-success",
    label: "Confirmado",
  },
  canceled: {
    token: "--app-danger",
    chip: "bg-app-danger/10 text-app-fg-strong",
    dot: "bg-app-danger",
    label: "Cancelado",
  },
  finished: {
    token: "--brand",
    chip: "bg-brand-soft text-app-fg-strong",
    dot: "bg-brand",
    label: "Finalizado",
  },
  attended: {
    token: "--brand",
    chip: "bg-brand-soft text-app-fg-strong",
    dot: "bg-brand",
    label: "Compareceu",
  },
};

const fallback: StatusConfig = {
  token: "--app-fg-subtle",
  chip: "bg-app-surface-muted text-app-fg",
  dot: "bg-app-fg-subtle",
  label: "",
};

export function getStatusConfig(status: string, outcome?: string | null): StatusConfig {
  if (outcome === "no_show") return { ...statusMap.canceled, label: "Não compareceu" };
  return statusMap[status?.toLowerCase() as SchedulingStatus] || fallback;
}

/** Inline style of a calendar event: soft ground, a coloured edge, neutral text. */
export function getStatusStyle(status: string): CSSProperties {
  const { token } = getStatusConfig(status);

  return {
    background: `hsl(var(${token}) / 0.12)`,
    color: "hsl(var(--app-fg-strong))",
    border: "none",
    borderLeft: `3px solid hsl(var(${token}))`,
    borderRadius: "6px",
    fontSize: "12px",
  };
}

export const statusIndicators: { value: SchedulingStatus; color: string }[] = [
  { value: "pending", color: statusMap.pending.dot },
  { value: "scheduled", color: statusMap.scheduled.dot },
  { value: "confirmed", color: statusMap.confirmed.dot },
  { value: "canceled", color: statusMap.canceled.dot },
  { value: "finished", color: statusMap.finished.dot },
];
